import React, { useContext, useEffect, useState } from 'react';
import { Glyph, GlyphLayer, IdeaBoxEntry, Project } from 'konect-model';
import { nanoid } from "nanoid";

import '../App.css';
import { AppContext, NavState } from '../App';
import UserControlService from '../../services/user-control-service';
import GlyphEditor from './GlyphEditor';
import GlyphGrouping from './GlyphGrouping';
import GlyphGroupingLegacy from './GlyphGroupingLegacy';
import { DefaultEventService } from '../../services/event-service';

interface GlyphSketchingProps {
    state: NavState;
}

export default function GlyphSketching(props: GlyphSketchingProps) {
    const { project } = useContext(AppContext);

    const [glyphs, setGlyphs] = useState([...removeDeletedEntries(project.glyphs, project.ideaBox.entries)]);
    const [activeGlyph, setActiveGlyph] = useState<Glyph | undefined>();

    const [backlog, setBacklog] = useState(getBacklog(project));
    useEffect(() => {
        setGlyphs([...removeDeletedEntries(project.glyphs, project.ideaBox.entries)]);
        setBacklog(getBacklog(project));
    }, [project])

    const removeGlyph = (id: string) => {
        UserControlService.update();
        const index = glyphs.findIndex((el: Glyph) => el.id === id);
        const glyph = glyphs[index];
        for (const layer of glyph.layers) {
            for (const entry of layer.entries) {
                backlog.push(entry);
            }
        }
        glyphs.splice(index, 1);
        project.glyphs = glyphs;
        setGlyphs([...glyphs]);
        setBacklog([...backlog]);
        DefaultEventService.dispatch("GLYPHS_ADDED", project.glyphs)
    }

    const addGlyph = (name: string, description: string) => {
        UserControlService.update();
        let glyph = new Glyph(nanoid(), name, description);
        glyph.layers.push(new GlyphLayer(nanoid()));
        project.addGlyph(glyph);
        setGlyphs([...project.glyphs]);
        DefaultEventService.dispatch("GLYPHS_ADDED", project.glyphs)
        return glyph;
    };

    const drawGlyph = (id: string) => {
        setActiveGlyph(glyphs.find((el: Glyph) => el.id === id));
        UserControlService.changeView(NavState.GLYPH_2);
    }

    const generateGlyphs = () => {
        UserControlService.update();
        project.generateGlyphs();
        setGlyphs([...project.glyphs]);
        setBacklog(getBacklog(project));
        DefaultEventService.dispatch("GLYPHS_ADDED", project.glyphs)
    }

    // IdeaBox/table grouping
    if (props.state === NavState.GLYPH_1B) {
        return (<GlyphGrouping
            project={project}
            glyphs={glyphs}
            setGlyphs={setGlyphs}
            onRemove={removeGlyph}
            backlog={backlog}
            setBacklog={setBacklog}
            onCreate={addGlyph}
            onDraw={drawGlyph}
            generateGlyphs={generateGlyphs}
        />)
    // drag and drop grouping
    } else if (props.state === NavState.GLYPH_1A) {
        return (<div style={{ height: "100%" }} className="d-flex">
            <GlyphGroupingLegacy
                project={project}
                glyphs={glyphs}
                setGlyphs={setGlyphs}
                onRemove={removeGlyph}
                backlog={backlog}
                setBacklog={setBacklog}
                onCreate={addGlyph}
                onDraw={drawGlyph}
                generateGlyphs={generateGlyphs}
            />
        </div>)
    } else if (props.state === NavState.GLYPH_2 || props.state === NavState.GLYPH_3) {
        let initialGlyph: Glyph = activeGlyph || project.glyphs[0];
        if (props.state === NavState.GLYPH_3) {
            initialGlyph = new Glyph(nanoid());
        }
        return <GlyphEditor initialGlyph={initialGlyph} compositionMode={props.state === NavState.GLYPH_3} />;
    } else {
        return (
            <div>
                Unknown State
            </div>

        );
    }
};

const removeDeletedEntries = (gls: Glyph[], newEntries: IdeaBoxEntry[]) => {
    for (const gly of gls) {
        for (const layer of gly.layers) {
            layer.entries = layer.entries.filter((entry: IdeaBoxEntry) =>
                newEntries.find(e => e.id === entry.id)
            )
        }
        gly.layers = gly.layers.filter((layer: GlyphLayer, index) => layer.entries.length > 0 || index >= gly.layers.length - 1);
    }
    return gls;
}

export function getBacklog(project: Project) {
    //Fill the backlogs with all entries currently not used in glyphs.
    let entries: IdeaBoxEntry[] = [];
    if (project.glyphs.length > 0) {
        for (const entry of [...project.ideaBox.entries]) {
            let task;
            for (const glyph of project.glyphs) {
                task = glyph.findTaskInLayers(entry);
                if (task) {
                    //Needs to be set to get changes from IdeaBox
                    Object.assign(task, entry);
                    break;
                }
            }
            /** use to forbid the listing of empty ideaboxentries
            if (!entry.insight || !entry.selectedAttribute || task) {
                continue;
            }
             */
            if (task) {
                continue;
            }
            entries.push(entry);
        }
    } else {
        /** use to forbid the listing of empty ideaboxentries
        entries = [...project.ideaBox.entries.filter((entry: IdeaBoxEntry) => entry.insight)];
         */
        entries = [...project.ideaBox.entries];
    }
    return entries;
}